import { Field, FieldProps } from 'formik';
import React, { ReactNode } from 'react';
import { InjectedIntl, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { TranslationHelpers } from '../../../../i18n/translationHelpers';
import ErrorMessage from '../../components/errorMessage/ErrorMessage';
import { IErrorMessage } from '../../components/errorMessage/errorMessage.type';
import InputField from '../../components/InputField';
import { COLORS, FONT_FAMILY } from '../../constants/styles';
import { enforceFormat, formatNationalAsString } from './PhoneNumberFormatting';
import { MAX_LENGTH, validateJPPhoneNumber } from './PhoneNumberValidation';
import { InfoTooltip } from '../../components/InfoTooltip';
import { PostalCodeValidation } from '../postalcode/PostalCodeValidation';

interface IJPPhoneNumberFieldProps {
    autoFocus?: boolean;
    fieldId: string;
    label: string;
    intl: InjectedIntl;
    tooltipText?: string;
    value?: string;
    required?: boolean;
    postalCode?: string;
}

interface IJPMobileNumberFieldState {
    initialDisplayValue: string;
    isTouched: boolean;
    focused?: boolean;
    maxLength: number;
}

const PhoneNumberFieldWrapper = styled.div`
    position: relative;
`;

const PhoneNumberFieldInputWrapper = styled.div`
    position: relative;

    &::after {
        background: ${COLORS.white};
        bottom: 0;
        color: ${COLORS.grey7};
        content: '';
        ${FONT_FAMILY.multipleRegularCss};
        height: 23px;
        left: 0;
        position: absolute;
        top: ${(props: { focused?: boolean }) => (props.focused ? '-3px' : '-2px')};
        margin: auto 0 auto 44px;
    }
`;

const PhoneNumberTooltipWrapper = styled.div`
    & > div {
        margin-bottom: -6px;
        margin-right: -12px;
    }
`;

class JPPhoneNumberField extends React.Component<
    IJPPhoneNumberFieldProps,
    IJPMobileNumberFieldState
> {
    private readonly ID: string;
    private readonly LABEL_ID: string;

    constructor(props: IJPPhoneNumberFieldProps) {
        super(props);

        const { autoFocus, value, fieldId, label } = this.props;

        this.ID = fieldId;
        this.LABEL_ID = `label.${label}`;
        const initialDisplayValue = enforceFormat(value || '');

        this.state = {
            focused: autoFocus,
            initialDisplayValue,
            isTouched: false,
            maxLength: MAX_LENGTH,
        };

        this.renderInputField = this.renderInputField.bind(this);
        this.validate = this.validate.bind(this);
    }

    public componentDidUpdate(prevProps: IJPPhoneNumberFieldProps) {
        const { value } = this.props;
        if (value && value !== prevProps.value) {
            this.setState({
                isTouched: true,
            });
        }
    }

    public render() {
        return <Field name={this.ID} validate={this.validate} render={this.renderInputField} />;
    }

    private validate(value: string): IErrorMessage {
        const { required, label, fieldId, postalCode } = this.props;
        const isNotOverSeasPostalCode = !PostalCodeValidation.isOverSeasPostalCode(
            postalCode ?? '',
        );
        if (!value && required) {
            return TranslationHelpers.getMessageDescriptor(`error.${label}.required`);
        }

        if (value && isNotOverSeasPostalCode && !validateJPPhoneNumber(value, fieldId)) {
            return TranslationHelpers.getMessageDescriptor(`error.${label}.invalid_jp`);
        }

        return '';
    }

    private renderInputField({
        form: { touched, errors, handleBlur, handleChange, setFieldValue },
    }: FieldProps): ReactNode {
        const { autoFocus, value, required, tooltipText } = this.props;
        const { focused, initialDisplayValue, isTouched, maxLength } = this.state;

        const errorMessage =
            (isTouched || (touched && touched[this.ID])) && errors && errors[this.ID]
                ? errors[this.ID]
                : undefined;

        const onFocus = () => {
            if (!isTouched) {
                setFieldValue(this.ID, initialDisplayValue);
            }
            this.setState({
                focused: true,
                isTouched: true,
            });
        };

        const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
            this.setState({
                focused: false,
            });
            handleBlur(event);
        };

        return (
            <PhoneNumberFieldWrapper data-qa-id={this.ID}>
                <PhoneNumberFieldInputWrapper focused={focused}>
                    <InputField
                        autoComplete="tel"
                        autoFocus={autoFocus}
                        id={this.ID}
                        labelMessage={TranslationHelpers.getMessageDescriptor(this.LABEL_ID)}
                        maxLength={maxLength}
                        value={isTouched ? value : initialDisplayValue}
                        required={required}
                        type="tel"
                        onBlur={onBlur}
                        onChange={(event) => {
                            event.target.value = formatNationalAsString(event.target.value);
                            handleChange(event);
                        }}
                        onFocus={onFocus}
                        error={errorMessage}
                    />
                    {tooltipText ? (
                        <InfoTooltip
                            id={this.ID}
                            tooltipText={TranslationHelpers.getMessageDescriptor(
                                'tooltip.phone_number',
                            )}
                            touchSize={[40, 40]}
                            uniqueId={this.ID}
                        />
                    ) : (
                        ''
                    )}
                </PhoneNumberFieldInputWrapper>
            </PhoneNumberFieldWrapper>
        );
    }
}

export default injectIntl(JPPhoneNumberField);
